'use client'

import { createStore } from 'zustand'
import { createContext, PropsWithChildren, useRef } from 'react'
import { Session } from 'next-auth'
import { Prescription } from '@/lib/questionnaires/Prescription'
import {
  CuratedMedicineConcise,
  PatientProfile,
  ProfileStatus,
} from '@/lib/apis/types'
import { SupportingDocument } from '@/features/prescriptions/SupportingDocumentItem'
import { uuidv4 } from 'uuidv7'
import { IHIVerificationState } from '@/shared/components/hi/HealthcareIdentifierForm'
import { PersonalDetailsFormState } from '@/shared/request/PersonalDetailsFormStage'
import { DefaultIHIVerificationState } from '@/shared/request/defaults'
import { computeProfileFormState } from '@/shared/request/utils'
import { DocumentUploadFormContext } from '@/shared/request/DocumentUploadFormContext'
import { IHIVerifyFormContext } from '@/shared/request/IHIVerifyFormContext'
import { PersonalDetailsFormContext } from '@/shared/request/PersonalDetailsFormContext'

export type SelectedMedicineData = {
  preferredBrand?: string
  medicine: CuratedMedicineConcise
}

type PrescriptionStageType =
  | 'PERSONAL-INFORMATION'
  | 'IHI-VERIFICATION'
  | 'QUESTIONNAIRE'
  | 'UPLOAD-DOCUMENTS'
  | 'REVIEW'

export type PrescriptionStage =
  | { type: Exclude<PrescriptionStageType, 'QUESTIONNAIRE'>; title: string }
  | { type: 'QUESTIONNAIRE'; title: string; page: number }

export interface ScriptRequestFormProps {
  preferredBrand?: string
  session: Session | null

  initialProfile: PatientProfile | null
  profileStatus: ProfileStatus | null

  selectedMedicine?: SelectedMedicineData
}

const DefaultProps = {
  requestId: uuidv4(),
  currentStageIdx: 0,
  questionnaireForm: {
    values: Prescription.Validator.cast({}),
    touched: new Set<string>(),
  },
  supportingDocuments: [],
  ihiVerificationState: DefaultIHIVerificationState,
  verifiedIdentifierAttachmentPending: false,
  verifiedIdentifierAttached: false,
}

interface ScriptRequestFormState extends ScriptRequestFormProps {
  requestId: string

  stages: Array<PrescriptionStage>
  currentStageIdx: number

  questionnaireForm: {
    values: Prescription.QuestionnaireValues
    touched: Set<string>
  }

  updateLoginState: (
    session: Session | null,
    profile: PatientProfile | null,
    profileStatus: ProfileStatus | null,
  ) => void
  reset: () => void

  onNext: () => void
  onPrev: () => void
  goToStage: (type: PrescriptionStageType, page?: number) => void

  // Questionnaire section
  updateQuestionnaireFormValues: (
    values: Prescription.QuestionnaireValues,
  ) => void

  // Medicine section
  updateSelectedMedicine: (medicine: SelectedMedicineData) => void

  // Document upload section
  supportingDocuments: Array<SupportingDocument>
  addSupportingDocument: (document: SupportingDocument) => void
  removeSupportingDocument: (name: string) => void
  updateSupportingDocument: (
    name: string,
    updater: () => Partial<SupportingDocument>,
  ) => void

  // IHI validation section
  ihiVerificationState: IHIVerificationState
  verifiedIdentifierAttachmentPending: boolean
  verifiedIdentifierAttached: boolean
  updateIHIVerificationState: (state: IHIVerificationState) => void
  setVerifiedIdentifierAttachmentPending: (pending: boolean) => void
  completeVerifiedIdentifierAttachment: (
    profileStatus: ProfileStatus | null,
  ) => void
  returnToIdentifierCorrection: () => void

  // Personal information section
  profileFormState: PersonalDetailsFormState
  updateProfileFormState: (
    updater: (state: PersonalDetailsFormState) => PersonalDetailsFormState,
  ) => void
}

type ScriptRequestFormStore = ReturnType<typeof createScriptRequestFormStore>

export const createScriptRequestFormStore = (
  initProps: ScriptRequestFormProps,
) => {
  const { session, initialProfile, profileStatus } = initProps

  const stages = computeStages(session, profileStatus)
  const profileFormState = computeProfileFormState(
    initialProfile,
    profileStatus,
  )

  return createStore<ScriptRequestFormState>()((set) => ({
    ...DefaultProps,
    ...initProps,
    stages,

    profileFormState,

    updateLoginState: (
      session: Session | null,
      profile: PatientProfile | null,
      profileStatus: ProfileStatus | null,
    ) =>
      set((state) => {
        const effectiveProfileStatus =
          state.verifiedIdentifierAttached &&
          profileStatus &&
          !profileStatus.ihiSubmitted
            ? { ...profileStatus, ihiSubmitted: true }
            : profileStatus
        const newStages = computeStages(session, effectiveProfileStatus)

        const previousStageType = state.stages[state.currentStageIdx].type
        let newStageIdx = state.currentStageIdx
        const shouldAttachVerifiedIdentifier =
          previousStageType === 'PERSONAL-INFORMATION' &&
          session !== null &&
          !effectiveProfileStatus?.ihiSubmitted &&
          Boolean(state.ihiVerificationState.verified)
        const keepPersonalInformationForIdentifierAttachment =
          previousStageType === 'PERSONAL-INFORMATION' &&
          session !== null &&
          (state.verifiedIdentifierAttachmentPending ||
            shouldAttachVerifiedIdentifier)
        const requiresIdentifierCorrection =
          previousStageType === 'PERSONAL-INFORMATION' &&
          session !== null &&
          !effectiveProfileStatus?.ihiSubmitted &&
          !shouldAttachVerifiedIdentifier &&
          !state.verifiedIdentifierAttachmentPending

        if (keepPersonalInformationForIdentifierAttachment) {
          newStageIdx = state.currentStageIdx
        } else if (requiresIdentifierCorrection) {
          const identifierStageIdx = newStages.findIndex(
            (stage) => stage.type === 'IHI-VERIFICATION',
          )
          if (identifierStageIdx >= 0) newStageIdx = identifierStageIdx
        } else if (
          previousStageType === 'IHI-VERIFICATION' &&
          effectiveProfileStatus?.ihiSubmitted
        ) {
          newStageIdx = findNextRemainingStageIndex(
            state.stages,
            newStages,
            state.currentStageIdx,
          )
        } else if (previousStageType === 'PERSONAL-INFORMATION') {
          const personalInformationStageIdx = newStages.findIndex(
            (stage) => stage.type === 'PERSONAL-INFORMATION',
          )
          newStageIdx =
            personalInformationStageIdx >= 0
              ? personalInformationStageIdx
              : findNextRemainingStageIndex(
                  state.stages,
                  newStages,
                  state.currentStageIdx,
                )
        }

        let ihiVerificationState = state.ihiVerificationState
        if (
          effectiveProfileStatus?.ihiSubmitted &&
          !keepPersonalInformationForIdentifierAttachment
        ) {
          // If ihi has already been submitted, reset the ihi state so that the submitter has the correct information
          // that we don't need to send the ihi information.
          ihiVerificationState = DefaultIHIVerificationState
        }

        const refreshedProfileFormState = computeProfileFormState(
          profile,
          profileStatus,
        )
        const profileFormState =
          previousStageType === 'PERSONAL-INFORMATION'
            ? {
                values: state.profileFormState.values,
                verification: refreshedProfileFormState.verification,
              }
            : refreshedProfileFormState

        return {
          currentStageIdx: newStageIdx,
          stages: keepPersonalInformationForIdentifierAttachment
            ? state.stages
            : newStages,
          profileFormState,
          initialProfile: profile,
          ihiVerificationState,
          profileStatus: effectiveProfileStatus,
          session,
          verifiedIdentifierAttached:
            state.verifiedIdentifierAttached ||
            Boolean(effectiveProfileStatus?.ihiSubmitted),
        }
      }),
    reset: () =>
      set({
        ...DefaultProps,
        ...initProps,
      }),

    onNext: () =>
      set((state) => ({ currentStageIdx: state.currentStageIdx + 1 })),
    onPrev: () =>
      set((state) => ({ currentStageIdx: state.currentStageIdx - 1 })),
    goToStage: (type: PrescriptionStageType, page?: number) =>
      set((state) => {
        const currentStageIdx = state.stages.findIndex(
          (stage) =>
            stage.type === type &&
            (stage.type !== 'QUESTIONNAIRE' ||
              page === undefined ||
              stage.page === page),
        )
        return currentStageIdx >= 0 ? { currentStageIdx } : {}
      }),

    updateQuestionnaireFormValues: (values: Prescription.QuestionnaireValues) =>
      set((state) => ({
        questionnaireForm: {
          values,
          touched: state.questionnaireForm.touched,
        },
      })),

    updateSelectedMedicine: (medicine: SelectedMedicineData) =>
      set(() => ({ selectedMedicine: medicine })),

    addSupportingDocument: (document: SupportingDocument) =>
      set((state) => ({
        supportingDocuments: [...state.supportingDocuments, document],
      })),
    removeSupportingDocument: (name: string) =>
      set((state) => ({
        supportingDocuments: state.supportingDocuments.filter(
          (doc) => doc.name !== name,
        ),
      })),
    updateSupportingDocument: (
      name: string,
      updater: () => Partial<SupportingDocument>,
    ) =>
      set((state) => ({
        supportingDocuments: state.supportingDocuments.map((doc) => {
          if (doc.name === name) return { ...doc, ...updater() }
          else return doc
        }),
      })),

    updateIHIVerificationState: (state: IHIVerificationState) =>
      set(() => ({ ihiVerificationState: state })),

    setVerifiedIdentifierAttachmentPending: (pending: boolean) =>
      set({ verifiedIdentifierAttachmentPending: pending }),

    completeVerifiedIdentifierAttachment: (
      refreshedProfileStatus: ProfileStatus | null,
    ) =>
      set((state) => {
        const nextProfileStatus = refreshedProfileStatus
          ? { ...refreshedProfileStatus, ihiSubmitted: true }
          : state.profileStatus
            ? { ...state.profileStatus, ihiSubmitted: true }
            : null
        const nextStages = computeStages(state.session, nextProfileStatus)
        const previousStage = state.stages[state.currentStageIdx]
        const matchingStageIdx = nextStages.findIndex(
          (stage) =>
            stage.type === previousStage.type &&
            (stage.type !== 'QUESTIONNAIRE' ||
              previousStage.type !== 'QUESTIONNAIRE' ||
              stage.page === previousStage.page),
        )

        return {
          currentStageIdx:
            matchingStageIdx >= 0
              ? matchingStageIdx
              : findNextRemainingStageIndex(
                  state.stages,
                  nextStages,
                  state.currentStageIdx,
                ),
          stages: nextStages,
          profileStatus: nextProfileStatus,
          ihiVerificationState: DefaultIHIVerificationState,
          verifiedIdentifierAttachmentPending: false,
          verifiedIdentifierAttached: true,
        }
      }),

    returnToIdentifierCorrection: () =>
      set((state) => {
        const identifierStageIdx = state.stages.findIndex(
          (stage) => stage.type === 'IHI-VERIFICATION',
        )
        return {
          currentStageIdx:
            identifierStageIdx >= 0
              ? identifierStageIdx
              : state.currentStageIdx,
          ihiVerificationState: {
            ...state.ihiVerificationState,
            verified: undefined,
          },
          verifiedIdentifierAttachmentPending: false,
          verifiedIdentifierAttached: false,
        }
      }),

    updateProfileFormState: (
      updater: (state: PersonalDetailsFormState) => PersonalDetailsFormState,
    ) =>
      set((state) => ({
        profileFormState: updater(state.profileFormState),
      })),
  }))
}

function computeStages(
  session: Session | null,
  profileStatus: ProfileStatus | null,
): Array<PrescriptionStage> {
  const stages: Array<PrescriptionStage> = [
    ...Prescription.Questionnaires.pages.map((_, page) => ({
      type: 'QUESTIONNAIRE' as const,
      title: 'Digital Consultation',
      page,
    })),
    { type: 'UPLOAD-DOCUMENTS' as const, title: 'Supporting Documents' },
  ]

  if (!profileStatus?.ihiSubmitted)
    stages.push({
      type: 'IHI-VERIFICATION' as const,
      title: 'Enter Medicare Details',
    })
  if (!session || !profileStatus?.completed || !profileStatus.mobileVerified)
    stages.push({
      type: 'PERSONAL-INFORMATION' as const,
      title: 'Add Your Details',
    })

  stages.push({ type: 'REVIEW' as const, title: 'Review Your Request' })
  return stages
}

function findNextRemainingStageIndex(
  previousStages: Array<PrescriptionStage>,
  nextStages: Array<PrescriptionStage>,
  previousStageIdx: number,
): number {
  for (
    let index = previousStageIdx + 1;
    index < previousStages.length;
    index++
  ) {
    const nextStageIdx = nextStages.findIndex(
      (stage) => stage.type === previousStages[index].type,
    )
    if (nextStageIdx >= 0) return nextStageIdx
  }

  return Math.max(0, nextStages.length - 1)
}

export const ScriptRequestFormContext =
  createContext<ScriptRequestFormStore | null>(null)

export default function ScriptRequestFormProvider({
  children,
  ...props
}: PropsWithChildren<ScriptRequestFormProps>) {
  const store = useRef<ScriptRequestFormStore | null>(null)

  if (!store.current) {
    store.current = createScriptRequestFormStore(props)
  }

  return (
    <ScriptRequestFormContext.Provider value={store.current}>
      <DocumentUploadFormContext.Provider value={store.current}>
        <IHIVerifyFormContext.Provider value={store.current}>
          <PersonalDetailsFormContext.Provider value={store.current}>
            {children}
          </PersonalDetailsFormContext.Provider>
        </IHIVerifyFormContext.Provider>
      </DocumentUploadFormContext.Provider>
    </ScriptRequestFormContext.Provider>
  )
}
